import type { FC } from 'react';
import { useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Full key variations for dynamic t() at lines 58, 61, 69, 76 (studio.modals.confirm.${variant}.*; variant in submit|edit|delete):
//   studio.modals.confirm.submit.title
//   studio.modals.confirm.submit.text
//   studio.modals.confirm.submit.checkbox.label
//   studio.modals.confirm.submit.confirmCta
//   studio.modals.confirm.edit.title
//   studio.modals.confirm.edit.text
//   studio.modals.confirm.edit.checkbox.label
//   studio.modals.confirm.edit.confirmCta
//   studio.modals.confirm.delete.title
//   studio.modals.confirm.delete.text
//   studio.modals.confirm.delete.checkbox.label
//   studio.modals.confirm.delete.confirmCta
import { BiData, BiEvent, StudioBatchUpdateBiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ConfirmationModal from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import { BatchAction } from '../../types/store';

import s from './BatchEditOrDeleteConfirmation.scss';

export interface Props {
  isOpen?: boolean;
  batchAction: BatchAction;
  selectedCount: number;
  confirmBtnBiData?: BiEvent;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'submit' | 'edit' | 'delete';
}

const BatchEditOrDeleteConfirmation: FC<Props> = ({
  isOpen,
  batchAction,
  selectedCount,
  confirmBtnBiData,
  onConfirm,
  onCancel,
  variant = 'edit',
}) => {
  const { t } = useTranslation();
  const isDesktop = !useContext(MobileContext);
  const isSubmitVariant = variant === 'submit';
  const isEditVariant = variant === 'edit';
  const isDeleteVariant = variant === 'delete';
  const requiresConfirmation = isDesktop && !isSubmitVariant;
  const [isConfirmable, setIsConfirmable] = useState(
    !requiresConfirmation || isEditVariant || isDeleteVariant // edit & delete is by default pre-selected T66847 / T67076
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toggleIsConfirmable = useCallback(() => {
    setIsConfirmable(!isConfirmable);
  }, [setIsConfirmable, isConfirmable]);

  const onConfirmClick = useCallback(() => {
    setIsSubmitting(true);
    onConfirm();
  }, [setIsSubmitting, onConfirm]);

  const i18nKeyBase = `studio.modals.confirm.${variant}`;

  return (
    <ConfirmationModal
      title={t(`${i18nKeyBase}.title`)}
      text={
        <div>
          {t(`${i18nKeyBase}.text`, { count: selectedCount })}
          {requiresConfirmation ? (
            <BooleanInput
              type="checkbox"
              size="small"
              id="edit-confirm"
              value={isConfirmable}
              className={s['checkbox']}
              label={t(`${i18nKeyBase}.checkbox.label`)}
              labelClassName={s['checkbox-label']}
              onChange={toggleIsConfirmable}
            />
          ) : null}
        </div>
      }
      confirmBtnLabel={t(`${i18nKeyBase}.confirmCta`)}
      cancelBtnLabel={t('common.cancel')}
      confirmBtnBiData={
        confirmBtnBiData ||
        BiData<StudioBatchUpdateBiEvent>({
          evid: 12,
          update_type: isSubmitVariant ? 'submit' : isDeleteVariant ? 'other_delete' : batchAction,
          items_count: selectedCount,
        })
      }
      confirmBtnType={variant === 'delete' ? 'danger' : 'default'}
      confirmBtnDisabled={isSubmitting || !isConfirmable}
      onConfirm={onConfirmClick}
      onCancel={onCancel}
      isOpen={isOpen}
    />
  );
};
BatchEditOrDeleteConfirmation.displayName = 'BatchEditOrDeleteConfirmation';

export default BatchEditOrDeleteConfirmation;
